// Visual helpers: the ball mesh, its flight trail, the aim reticle and trajectory preview,
// confetti and turf particles. Buffers are allocated once and reused every frame.
import * as THREE from 'three';
import { BALL_R } from './config.js';
import { ballTextures } from './textures.js';

const _q = new THREE.Quaternion();
const _v = new THREE.Vector3();
const _w = new THREE.Vector3();

export class BallView {
  constructor(scene) {
    const { map, bump } = ballTextures();
    this.textures = [map, bump];
    this.material = new THREE.MeshPhysicalMaterial({ map, bumpMap: bump, bumpScale: 1.2, roughness: 0.42, clearcoat: 0.6, clearcoatRoughness: 0.35 });
    this.geometry = new THREE.SphereGeometry(BALL_R, 48, 32);
    this.mesh = new THREE.Mesh(this.geometry, this.material);
    this.mesh.castShadow = true;
    scene.add(this.mesh);

    // ribbon trail facing the camera
    this.trailMax = 36;
    this.trailPts = [];
    const n = this.trailMax;
    this.trailPos = new Float32Array(n * 2 * 3);
    this.trailAlpha = new Float32Array(n * 2);
    const idx = [];
    for (let i = 0; i < n - 1; i++) { const a = i * 2; idx.push(a, a + 1, a + 2, a + 1, a + 3, a + 2); }
    this.trailGeo = new THREE.BufferGeometry();
    this.trailGeo.setAttribute('position', new THREE.BufferAttribute(this.trailPos, 3).setUsage(THREE.DynamicDrawUsage));
    this.trailGeo.setAttribute('alpha', new THREE.BufferAttribute(this.trailAlpha, 1).setUsage(THREE.DynamicDrawUsage));
    this.trailGeo.setIndex(idx);
    this.trailMat = new THREE.ShaderMaterial({
      transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide,
      uniforms: { color: { value: new THREE.Color('#d8fd71') } },
      vertexShader: 'attribute float alpha; varying float vA; void main(){ vA = alpha; gl_Position = projectionMatrix * modelViewMatrix * vec4(position,1.0); }',
      fragmentShader: 'uniform vec3 color; varying float vA; void main(){ gl_FragColor = vec4(color * vA, vA); }',
    });
    this.trail = new THREE.Mesh(this.trailGeo, this.trailMat);
    this.trail.frustumCulled = false;
    this.trail.visible = false;
    scene.add(this.trail);
  }

  /** Integrate the visual orientation from the physical spin. */
  sync(body, dt) {
    this.mesh.position.copy(body.pos);
    const w = body.spin.length();
    if (w > 1e-4) {
      _q.setFromAxisAngle(_v.copy(body.spin).multiplyScalar(1 / w), w * dt);
      this.mesh.quaternion.premultiply(_q);
    }
  }

  clearTrail() { this.trailPts.length = 0; this.trail.visible = false; }

  updateTrail(camera, emitting, speed) {
    if (emitting && speed > 8) {
      this.trailPts.push(this.mesh.position.clone());
      if (this.trailPts.length > this.trailMax) this.trailPts.shift();
    } else if (this.trailPts.length) {
      this.trailPts.shift();
    }
    const pts = this.trailPts;
    this.trail.visible = pts.length > 2;
    if (!this.trail.visible) return;
    const n = this.trailMax;
    for (let i = 0; i < n; i++) {
      const p = pts[Math.min(i, pts.length - 1)];
      const next = pts[Math.min(i + 1, pts.length - 1)];
      const prev = pts[Math.min(Math.max(i - 1, 0), pts.length - 1)];
      _v.subVectors(next, prev);
      _w.subVectors(camera.position, p).cross(_v).normalize();
      const f = i < pts.length ? i / (pts.length - 1) : 1;
      const width = BALL_R * 0.9 * f;
      const k = i * 6;
      this.trailPos[k] = p.x + _w.x * width; this.trailPos[k + 1] = p.y + _w.y * width; this.trailPos[k + 2] = p.z + _w.z * width;
      this.trailPos[k + 3] = p.x - _w.x * width; this.trailPos[k + 4] = p.y - _w.y * width; this.trailPos[k + 5] = p.z - _w.z * width;
      const a = i < pts.length ? f * f * 0.55 : 0;
      this.trailAlpha[i * 2] = a; this.trailAlpha[i * 2 + 1] = a;
    }
    this.trailGeo.attributes.position.needsUpdate = true;
    this.trailGeo.attributes.alpha.needsUpdate = true;
  }

  dispose() {
    for (const t of this.textures) t.dispose();
    this.material.dispose(); this.geometry.dispose(); this.trailGeo.dispose(); this.trailMat.dispose();
  }
}

/** Target reticle on the goal plane plus a dotted, partial trajectory preview. */
export class AimView {
  constructor(scene) {
    this.group = new THREE.Group();
    const mat = (c, o) => new THREE.MeshBasicMaterial({ color: c, transparent: true, opacity: o, depthTest: false, depthWrite: false, toneMapped: false });
    this.ringMat = mat('#d8fd71', 0.95);
    this.ring = new THREE.Mesh(new THREE.RingGeometry(0.2, 0.25, 40), this.ringMat);
    this.inner = new THREE.Mesh(new THREE.CircleGeometry(0.045, 16), mat('#ffffff', 0.9));
    this.halo = new THREE.Mesh(new THREE.RingGeometry(0.25, 0.42, 40), mat('#d8fd71', 0.16));
    const tick = new THREE.PlaneGeometry(0.03, 0.11);
    for (let i = 0; i < 4; i++) {
      const m = new THREE.Mesh(tick, this.ringMat);
      const a = (i * Math.PI) / 2;
      m.position.set(Math.sin(a) * 0.32, Math.cos(a) * 0.32, 0);
      m.rotation.z = -a;
      this.group.add(m);
    }
    this.group.add(this.ring, this.inner, this.halo);
    this.group.renderOrder = 10;
    this.group.traverse((o) => { o.renderOrder = 10; });
    scene.add(this.group);

    this.dotCount = 22;
    this.dots = new THREE.InstancedMesh(new THREE.SphereGeometry(0.035, 8, 6), new THREE.MeshBasicMaterial({ color: '#ffffff', transparent: true, opacity: 0.6, depthWrite: false, toneMapped: false }), this.dotCount);
    this.dots.frustumCulled = false;
    this.dots.renderOrder = 9;
    scene.add(this.dots);
    this.m4 = new THREE.Matrix4();
  }

  setVisible(v) { this.group.visible = v; this.dots.visible = v && this.previewOn !== false; }

  setTarget(x, y, pulse) {
    this.group.position.set(x, y, 0.05);
    const s = 1 + pulse * 0.25;
    this.group.scale.set(s, s, s);
  }

  setColor(c) { this.ringMat.color.set(c); }

  /** Show the first part of the path only: helps reading the shot without giving it away. */
  setPath(path, fraction = 0.42) {
    const n = Math.max(0, Math.floor(path.length * fraction));
    for (let i = 0; i < this.dotCount; i++) {
      const src = path[Math.floor((i / this.dotCount) * n)];
      if (!src || n < 2) { this.m4.makeScale(0, 0, 0); } else {
        const fade = 1 - i / this.dotCount;
        this.m4.makeScale(fade + 0.3, fade + 0.3, fade + 0.3).setPosition(src);
      }
      this.dots.setMatrixAt(i, this.m4);
    }
    this.dots.instanceMatrix.needsUpdate = true;
  }
}

/** Pooled particle bursts: confetti for goals and turf for kicks. */
export class Particles {
  constructor(scene, max = 900) {
    this.max = max;
    this.pos = new Float32Array(max * 3);
    this.col = new Float32Array(max * 3);
    this.vel = new Float32Array(max * 3);
    this.life = new Float32Array(max);
    this.drag = new Float32Array(max);
    this.geo = new THREE.BufferGeometry();
    this.geo.setAttribute('position', new THREE.BufferAttribute(this.pos, 3).setUsage(THREE.DynamicDrawUsage));
    this.geo.setAttribute('color', new THREE.BufferAttribute(this.col, 3).setUsage(THREE.DynamicDrawUsage));
    this.mat = new THREE.PointsMaterial({ size: 0.12, vertexColors: true, transparent: true, depthWrite: false, sizeAttenuation: true });
    this.points = new THREE.Points(this.geo, this.mat);
    this.points.frustumCulled = false;
    scene.add(this.points);
    this.cursor = 0;
    this.active = 0;
    for (let i = 0; i < max; i++) this.pos[i * 3 + 1] = -100;
  }

  spawn(p, v, color, life, drag = 0.4) {
    const i = this.cursor;
    this.cursor = (this.cursor + 1) % this.max;
    this.pos.set([p.x, p.y, p.z], i * 3);
    this.vel.set([v.x, v.y, v.z], i * 3);
    this.col.set([color.r, color.g, color.b], i * 3);
    this.life[i] = life;
    this.drag[i] = drag;
    this.active = Math.min(this.max, this.active + 1);
  }

  confetti(center, count = 320) {
    const cols = ['#d8fd71', '#ffffff', '#e8313f', '#18c7d8', '#f5c542'].map((c) => new THREE.Color(c));
    for (let i = 0; i < count; i++) {
      _v.set(center.x + (Math.random() - 0.5) * 9, center.y + Math.random() * 1.5, center.z + (Math.random() - 0.5) * 2);
      _w.set((Math.random() - 0.5) * 6, 5 + Math.random() * 7, 2 + Math.random() * 5);
      this.spawn(_v, _w, cols[i % cols.length], 2.5 + Math.random() * 1.5, 1.6);
    }
  }

  turf(p, dir, count = 26) {
    const c = new THREE.Color();
    for (let i = 0; i < count; i++) {
      c.setRGB(0.15 + Math.random() * 0.1, 0.35 + Math.random() * 0.2, 0.12);
      _w.set(dir.x * 2 + (Math.random() - 0.5) * 2, 1 + Math.random() * 2.2, dir.z * 2 + (Math.random() - 0.5) * 2);
      this.spawn(p, _w, c, 0.6 + Math.random() * 0.4, 1.2);
    }
  }

  update(dt) {
    if (!this.active) return;
    let alive = 0;
    for (let i = 0; i < this.max; i++) {
      if (this.life[i] <= 0) continue;
      this.life[i] -= dt;
      const k = i * 3;
      if (this.life[i] <= 0) { this.pos[k + 1] = -100; continue; }
      alive++;
      const d = Math.exp(-this.drag[i] * dt);
      this.vel[k] *= d; this.vel[k + 2] *= d;
      this.vel[k + 1] = this.vel[k + 1] * d - 9.81 * dt * (this.drag[i] > 1.5 ? 0.25 : 1);
      this.pos[k] += this.vel[k] * dt;
      this.pos[k + 1] += this.vel[k + 1] * dt;
      this.pos[k + 2] += this.vel[k + 2] * dt;
      if (this.pos[k + 1] < 0.02) { this.pos[k + 1] = 0.02; this.vel[k] = this.vel[k + 1] = this.vel[k + 2] = 0; }
    }
    this.active = alive;
    this.geo.attributes.position.needsUpdate = true;
    this.geo.attributes.color.needsUpdate = true;
  }

  clear() { this.life.fill(0); for (let i = 0; i < this.max; i++) this.pos[i * 3 + 1] = -100; this.active = 0; this.geo.attributes.position.needsUpdate = true; }
}
